import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface NotificationContextType {
  showNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const id = Math.random().toString(36).substr(2, 9);
    setNotification({ id, message, type });

    // Auto-hide after 3 seconds
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
      timeoutRef.current = null;
    }, 3000);
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {notification && (
        <NotificationContainer>
          <NotificationContent type={notification.type}>
            {notification.message}
          </NotificationContent>
        </NotificationContainer>
      )}
    </NotificationContext.Provider>
  );
};

// Styled Components
const slideDown = keyframes`
  from {
    transform: translateY(-120%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 3rem;
  z-index: 10001;
  animation: ${slideDown} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;

  @media (max-width: 768px) {
    top: auto;
    bottom: 24px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    animation: ${slideUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const NotificationContent = styled.div<{ type: 'success' | 'error' | 'info' }>`
  background: ${props => {
    switch (props.type) {
      case 'success':
        return '#1a1a1a';
      case 'error':
        return '#dc2626';
      case 'info':
        return '#3b82f6';
      default:
        return '#1a1a1a';
    }
  }};
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 0.875rem;
    border-radius: 50px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    min-width: 200px;
  }
`;
